'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  CookieConsentStore,
  CategoryConsent,
  CookieConsentState
} from './types'
import {
  CONSENT_VERSION,
  DEFAULT_CATEGORY_CONSENT,
  needsMigration,
  migrateFromLegacyCategories
} from './types'

/**
 * Default consent state - user has not made a decision yet
 */
const defaultState: CookieConsentState = {
  hasConsented: false,
  status: 'pending',
  categories: DEFAULT_CATEGORY_CONSENT,
  consentedAt: null,
  consentVersion: CONSENT_VERSION,
  consentMethod: null,
  _hasSyncedFromBackend: false
}

/**
 * Internal Zustand store for cookie consent
 *
 * IMPORTANT: Do not use this directly! Use useCookieConsent() hook instead.
 *
 * This store contains only raw state management.
 * The public hook wraps this and adds business logic + backend sync.
 */
export const useCookieConsentStore = create<CookieConsentStore>()(
  persist(
    (set) => ({
      // State
      ...defaultState,

      // Simple setters (internal use only)
      setCategories: (categories: Partial<CategoryConsent>) =>
        set((state) => ({
          categories: {
            ...state.categories,
            ...categories,
            essential: true // Always ensure essential is true
          }
        })),

      setConsentState: (newState: Partial<CookieConsentState>) =>
        set((state) => ({
          ...state,
          ...newState,
          // Ensure essential is always true if categories are being set
          categories: newState.categories
            ? { ...newState.categories, essential: true }
            : state.categories
        })),

      reset: () => set((state) => ({
        ...defaultState,
        // Keep the sync flag true to prevent re-syncing from backend after reset
        _hasSyncedFromBackend: state._hasSyncedFromBackend
      })),

      setHasSyncedFromBackend: (value: boolean) => set({ _hasSyncedFromBackend: value })
    }),
    {
      name: 'cookie-consent-state', // localStorage key (same as before for compatibility)
      // Migrate from old 4-category format if needed
      migrate: (persistedState, version) => {
        const state = persistedState as CookieConsentState | undefined

        // No persisted state - use default
        if (!state) {
          return defaultState
        }

        // Check if migration is needed (old 4-category format)
        // Cast through unknown to avoid strict type checking on persisted data shape
        const storedCategories = state.categories as unknown as Record<string, boolean>
        if (storedCategories && needsMigration(storedCategories)) {
          console.log('[CookieConsent] Migrating from legacy 4-category format')
          return {
            ...state,
            categories: migrateFromLegacyCategories(storedCategories as {
              necessary?: boolean
              analytics?: boolean
              functionality?: boolean
              marketing?: boolean
            }),
            consentVersion: CONSENT_VERSION,
            consentMethod: state.consentMethod ?? 'banner', // Default for legacy
            _hasSyncedFromBackend: false
          }
        }

        // Already in new format or no categories - return as-is with version update
        return {
          ...state,
          consentVersion: state.consentVersion || CONSENT_VERSION,
          // Ensure new field has a default value for persisted state
          _hasSyncedFromBackend: false
        }
      },
      version: 3, // Bump version to trigger migration for new field
      // Don't persist _hasSyncedFromBackend - it's session-only
      partialize: (state) => ({
        hasConsented: state.hasConsented,
        status: state.status,
        categories: state.categories,
        consentedAt: state.consentedAt,
        consentVersion: state.consentVersion,
        consentMethod: state.consentMethod
        // _hasSyncedFromBackend is intentionally excluded - session-only flag
      })
    }
  )
)
